import { useState, useEffect } from 'react'
import { Users, Target, Briefcase, FileCheck, Mail, User } from 'lucide-react'

interface TeamMember {
  id: string
  name: string
  email: string
  role: string
  status: string
  avatar_url: string | null
  created_at: string
  client_count: number
  deal_count: number
  compliance_count: number
  okr_count: number
}

interface Summary {
  total_members: number
  active: number
  advisors: number
  analysts: number
  total_clients: number
  total_deals: number
}

export default function TeamPage() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [roleFilter, setRoleFilter] = useState<string>('')

  useEffect(() => {
    fetchTeam()
  }, [roleFilter])

  const fetchTeam = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (roleFilter) params.append('role', roleFilter)
      params.append('status', 'active')

      const response = await fetch(`/api/team?${params.toString()}`)
      const data = await response.json()
      
      if (data.success) {
        setMembers(data.data)
        setSummary(data.summary)
      }
    } catch (error) {
      console.error('Error fetching team members:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'text-purple-600 bg-purple-100'
      case 'advisor': return 'text-royal-blue-600 bg-royal-blue-100'
      case 'analyst': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-royal-blue"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-dashboard-title text-royal-blue-700 mb-2">Team Directory</h1>
        <p className="text-graphite-grey-500">
          Manage team members and track individual contributions across the organization
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-royal-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-royal-blue-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Team Members</p>
            <p className="text-3xl font-bold text-royal-blue">{summary.total_members}</p>
            <p className="text-xs text-green-600 mt-1">{summary.active} active</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Total Clients</p>
            <p className="text-3xl font-bold text-green-600">{summary.total_clients}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-golden-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-golden-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Active Deals</p>
            <p className="text-3xl font-bold text-golden">{summary.total_deals}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Avg per Member</p>
            <p className="text-3xl font-bold text-purple-600">
              {summary.total_members > 0 ? (summary.total_clients / summary.total_members).toFixed(1) : '0'}
            </p>
            <p className="text-xs text-graphite-grey mt-1">clients</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={() => setRoleFilter('')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              roleFilter === '' 
                ? 'bg-royal-blue text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            All Roles
          </button>
          <button
            onClick={() => setRoleFilter('admin')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              roleFilter === 'admin' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            Admins
          </button>
          <button
            onClick={() => setRoleFilter('advisor')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              roleFilter === 'advisor' 
                ? 'bg-royal-blue text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            Advisors
          </button>
          <button
            onClick={() => setRoleFilter('analyst')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              roleFilter === 'analyst' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            Analysts
          </button>
        </div>
      </div>

      {/* Team Member Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map(member => (
          <div
            key={member.id}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            {/* Member Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-royal-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                {member.avatar_url ? (
                  <img 
                    src={member.avatar_url} 
                    alt={member.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-xl font-bold text-royal-blue-600">
                    {getInitials(member.name)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-vampire-black truncate">
                  {member.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-graphite-grey" />
                  <p className="text-sm text-graphite-grey truncate">{member.email}</p>
                </div>
              </div>
            </div>

            {/* Role Badge */}
            <div className="mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </span>
            </div>

            {/* Activity Metrics */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-graphite-grey" />
                  <p className="text-xs text-graphite-grey">Clients</p>
                </div>
                <p className="text-lg font-semibold text-royal-blue">{member.client_count}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-graphite-grey" />
                  <p className="text-xs text-graphite-grey">Deals</p>
                </div>
                <p className="text-lg font-semibold text-golden">{member.deal_count}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileCheck className="w-4 h-4 text-graphite-grey" />
                  <p className="text-xs text-graphite-grey">Compliance</p>
                </div>
                <p className="text-lg font-semibold text-green-600">{member.compliance_count}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-graphite-grey" />
                  <p className="text-xs text-graphite-grey">OKRs</p>
                </div>
                <p className="text-lg font-semibold text-purple-600">{member.okr_count}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {members.length === 0 && (
        <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-graphite-grey">No team members found</p>
        </div>
      )}
    </div>
  )
}
